/**
 * MuaqqibProfileAdminPage — STAS-only page to edit the legal-representative's
 * identity and the Najiz Power-of-Attorney attachment (image / PDF). Clients
 * see this data on the portal whenever their invoice includes a muaqqib
 * line-item.
 */
import { useEffect, useState } from "react";
import { Loader2, Upload, Trash2, Save, UserCheck } from "lucide-react";
import { api, formatApiError, API_BASE } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";

export default function MuaqqibProfileAdminPage() {
  const { theme } = useI18n();
  const [form, setForm] = useState({ full_name: "", national_id: "", date_of_birth: "", phone: "", najiz_note_ar: "", najiz_note_en: "" });
  const [poa, setPoa] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/muaqqib-profile");
      setForm({
        full_name: data.full_name || "",
        national_id: data.national_id || "",
        date_of_birth: data.date_of_birth || "",
        phone: data.phone || "",
        najiz_note_ar: data.najiz_note_ar || "",
        najiz_note_en: data.najiz_note_en || "",
      });
      setPoa(data.poa_attachment_present ? {
        filename: data.poa_attachment_filename,
        size: data.poa_attachment_size,
        uploaded_at: data.poa_attachment_uploaded_at,
      } : null);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      await api.patch("/admin/muaqqib-profile", form);
      setMsg("تم الحفظ ✓");
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  const uploadPoa = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/muaqqib-profile/poa", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg("تم رفع الوكالة الشرعية");
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  const deletePoa = async () => {
    if (!window.confirm("حذف صورة الوكالة الشرعية؟")) return;
    setBusy(true); setErr("");
    try { await api.delete("/admin/muaqqib-profile/poa"); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={css.page(theme)} data-testid="muaqqib-admin-page">
      <header style={{ marginBottom: 18 }}>
        <h1 style={{ ...css.h1, color: theme.gold }}>
          <UserCheck size={22} style={{ verticalAlign: "-4px", marginInlineEnd: 6 }} />
          بيانات المعقّب (الوكيل الشرعي)
        </h1>
        <p style={{ fontSize: 12, opacity: 0.75, maxWidth: 640, lineHeight: 1.8 }}>
          تظهر هذه البيانات تلقائياً لكل عميل عنده بند معقّب في فاتورته، حتى
          يتمكن من إصدار وكالة شرعية في ناجز. الحقول هنا سجل وحيد للنظام كله.
        </p>
      </header>
      <div style={css.card(theme)}>
        <Field label="الاسم الكامل" v={form.full_name} on={(v) => setForm((s) => ({ ...s, full_name: v }))} t="muaqqib-name" />
        <Field label="رقم الهوية الوطنية" v={form.national_id} on={(v) => setForm((s) => ({ ...s, national_id: v }))} ltr t="muaqqib-id" />
        <Field label="تاريخ الميلاد (YYYY-MM-DD)" v={form.date_of_birth} on={(v) => setForm((s) => ({ ...s, date_of_birth: v }))} ltr t="muaqqib-dob" placeholder="1985-03-15" />
        <Field label="رقم الجوال (للسجل الداخلي — لا يظهر للعميل)" v={form.phone} on={(v) => setForm((s) => ({ ...s, phone: v }))} ltr t="muaqqib-phone" />
        <FieldArea label="ملاحظة ترشد العميل لكيفية التوكيل (عربي)" v={form.najiz_note_ar} on={(v) => setForm((s) => ({ ...s, najiz_note_ar: v }))} t="muaqqib-note-ar" />
        <FieldArea label="ملاحظة بالإنجليزية (اختياري)" v={form.najiz_note_en} on={(v) => setForm((s) => ({ ...s, najiz_note_en: v }))} t="muaqqib-note-en" />

        <div style={{ marginTop: 18, borderTop: `1px solid ${theme.borderSoft}`, paddingTop: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>صورة الوكالة الشرعية (ناجز)</div>
          {poa ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 10, background: theme.surfaceSubtle, borderRadius: 8 }}>
              <div style={{ fontSize: 12 }}>
                <strong>{poa.filename}</strong> · {Math.round((poa.size || 0) / 1024)} KB
                <div style={{ fontSize: 10, opacity: 0.6, fontFamily: "monospace" }} dir="ltr">{poa.uploaded_at}</div>
              </div>
              <button onClick={deletePoa} style={css.btnDanger} data-testid="muaqqib-delete-poa"><Trash2 size={14} /></button>
            </div>
          ) : (
            <div style={{ fontSize: 12, opacity: 0.6 }}>لم تُرفع وكالة شرعية بعد</div>
          )}
          <label style={{ ...css.btnGold(theme), display: "inline-flex", marginTop: 8, cursor: "pointer" }} data-testid="muaqqib-upload-poa">
            <Upload size={14} strokeWidth={2.4} />
            {poa ? "استبدال" : "رفع صورة الوكالة"}
            <input type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => uploadPoa(e.target.files?.[0])} />
          </label>
        </div>
        {msg && <div style={{ color: "#16a34a", marginTop: 12, fontSize: 12 }} data-testid="muaqqib-success">{msg}</div>}
        {err && <div style={{ color: "#dc2626", marginTop: 12, fontSize: 12 }}>{err}</div>}

        <button onClick={save} disabled={busy} style={{ ...css.btnGold(theme), marginTop: 18 }} data-testid="muaqqib-save">
          {busy ? <Loader2 size={14} className="vta-spin" /> : <Save size={14} strokeWidth={2.4} />}
          حفظ
        </button>
      </div>
      <style>{`.vta-spin{animation:vta-spin 1s linear infinite}@keyframes vta-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function Field({ label, v, on, ltr, t, placeholder }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, opacity: 0.75, marginBottom: 4 }}>{label}</label>
      <input value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder}
        style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)", background: "rgba(255,255,255,0.04)", color: "inherit", fontSize: 13, direction: ltr ? "ltr" : "rtl", boxSizing: "border-box", outline: "none" }}
        data-testid={t} />
    </div>
  );
}

function FieldArea({ label, v, on, t }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, opacity: 0.75, marginBottom: 4 }}>{label}</label>
      <textarea value={v} rows={2} onChange={(e) => on(e.target.value)}
        style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)", background: "rgba(255,255,255,0.04)", color: "inherit", fontSize: 13, boxSizing: "border-box", outline: "none", fontFamily: "inherit", resize: "vertical" }}
        data-testid={t} />
    </div>
  );
}

const css = {
  page: (theme) => ({ maxWidth: 720, margin: "0 auto", padding: 18, color: theme.text }),
  h1: { fontSize: 20, fontWeight: 900, margin: 0 },
  card: (theme) => ({ background: theme.surface, padding: 22, borderRadius: 16, border: `1px solid ${theme.borderSoft}` }),
  btnGold: (theme) => ({ background: theme.gold, color: "#0a0a0a", border: "none", padding: "10px 18px", borderRadius: 10, fontWeight: 900, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }),
  btnDanger: { background: "rgba(220,38,38,0.10)", color: "#dc2626", border: "1px solid #dc2626", padding: 6, borderRadius: 6, cursor: "pointer" },
};
